import React, { useState, useEffect } from 'react';
import { Shield, Check, X, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND = window.__BACKEND_URL__ || process.env.REACT_APP_BACKEND_URL;

const VerifyRonde = () => {
  const [rondeId, setRondeId] = useState('');
  const [hash, setHash] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // Auto-fill depuis ?id=X&hash=Y (deep link QR code)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('id');
    const h = params.get('hash');
    if (id) setRondeId(id);
    if (h) setHash(h);
    // Auto-submit si les deux presents
    if (id && h) {
      (async () => {
        setLoading(true);
        try {
          const { data } = await axios.post(`${BACKEND}/api/public/verify-ronde`, {
            ronde_id: id.trim(),
            provided_hash: h.trim().toLowerCase(),
          });
          setResult(data);
        } catch (err) {
          setResult({ error: err?.response?.data?.detail || 'Erreur' });
        } finally {
          setLoading(false);
        }
      })();
    }
  }, []);

  const verify = async (e) => {
    e.preventDefault();
    setLoading(true); setResult(null);
    try {
      const { data } = await axios.post(`${BACKEND}/api/public/verify-ronde`, {
        ronde_id: rondeId.trim(),
        provided_hash: hash.trim().toLowerCase(),
      });
      setResult(data);
    } catch (err) {
      setResult({ error: err?.response?.data?.detail || 'Erreur' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-900 flex items-center justify-center p-6">
      <div className="w-full max-w-xl bg-white rounded-2xl shadow-2xl p-8" data-testid="verify-page">
        <div className="flex items-center gap-3 mb-2">
          <Shield className="w-8 h-8 text-cyan-600" />
          <h1 className="text-2xl font-bold text-slate-900">Verification d'integrite</h1>
        </div>
        <p className="text-sm text-slate-600 mb-6">
          Verifiez qu'un rapport de ronde n'a pas ete altere en collant son identifiant et sa signature SHA-256.
          Ce service est <strong>public</strong> et ne divulgue aucune donnee sensible.
        </p>

        <form onSubmit={verify} className="space-y-4">
          <label className="block">
            <span className="text-xs font-medium text-slate-700 uppercase tracking-wide">Identifiant de la ronde</span>
            <input
              type="text"
              required
              value={rondeId}
              onChange={(e) => setRondeId(e.target.value)}
              placeholder="ex: 8f2a1b3c-..."
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm font-mono"
              data-testid="verify-ronde-id"
            />
          </label>

          <label className="block">
            <span className="text-xs font-medium text-slate-700 uppercase tracking-wide">Signature SHA-256 (64 caracteres)</span>
            <input
              type="text"
              required
              value={hash}
              onChange={(e) => setHash(e.target.value)}
              placeholder="ex: a1b2c3d4..."
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-xs font-mono"
              data-testid="verify-hash"
            />
          </label>

          <button
            type="submit"
            disabled={loading || !rondeId || !hash}
            className="w-full py-2.5 px-4 bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-white rounded-lg font-medium text-sm inline-flex items-center justify-center gap-2"
            data-testid="verify-submit"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {loading ? 'Verification...' : 'Verifier'}
          </button>
        </form>

        {result && (
          <div className="mt-6">
            {result.error ? (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="verify-error">
                {result.error}
              </div>
            ) : !result.found ? (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm" data-testid="verify-not-found">
                Aucune ronde trouvee avec cet identifiant.
              </div>
            ) : result.valid ? (
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="verify-valid">
                <div className="flex items-center gap-2 text-emerald-800 font-semibold">
                  <Check className="w-5 h-5" /> Rapport authentique et intact
                </div>
                <div className="mt-2 text-xs text-slate-600 space-y-0.5">
                  <p>Ronde: <strong>{result.ronde_numero || result.ronde_id}</strong></p>
                  <p>Debut: {result.date_debut} - Fin: {result.date_fin}</p>
                  <p>Points de controle verifies: {result.nb_points}</p>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg" data-testid="verify-invalid">
                <div className="flex items-center gap-2 text-red-800 font-semibold">
                  <X className="w-5 h-5" /> Signature invalide - rapport altere
                </div>
                <p className="mt-2 text-xs text-slate-600">
                  Le hash fourni ne correspond pas au rapport officiel.
                  {result.expected_hash_hint && (
                    <> Attendu commence par: <code className="bg-white px-1 rounded">{result.expected_hash_hint}</code></>
                  )}
                </p>
              </div>
            )}
          </div>
        )}

        <p className="mt-6 text-center text-xs text-slate-400">
          Signature calculee via SHA-256 sur les donnees de la ronde au moment de sa cloture.
        </p>
      </div>
    </div>
  );
};

export default VerifyRonde;
